/**
 * Logout cleanup helpers for browser storage.
 *
 * We clear stored auth/session artifacts on sign-out to avoid leaking data between accounts.
 * Cookie/consent preferences are device/browser-scoped (not user-scoped), so we preserve them.
 */
const PRESERVE_LOCAL_STORAGE_KEYS = ['cmpConsentState', 'cookieConsent'];

/**
 * Safe wrapper for reading from a Web Storage object.
 *
 * Some environments throw on storage access (Safari private mode, strict privacy settings, etc.).
 *
 * @param {Storage} storage
 * @param {string} key
 * @returns {string|null}
 */
const safeGet = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
};

/**
 * Safe wrapper for writing to a Web Storage object.
 * @param {Storage} storage
 * @param {string} key
 * @param {string} value
 */
const safeSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch (e) {
    // ignore
  }
};

/**
 * Safe wrapper for clearing a Web Storage object.
 * @param {Storage} storage
 */
const safeClear = (storage) => {
  try {
    storage.clear();
  } catch (e) {
    // ignore
  }
};

// Logout cleanup should not wipe cookie consent that applies to the device/browser,
// not to a specific authenticated session.
/**
 * Clear auth-related web storage while preserving cookie/consent keys.
 *
 * Data flow:
 * - Read and stash known consent keys from `localStorage`.
 * - Clear both `localStorage` and `sessionStorage` to remove session artifacts.
 * - Restore the preserved consent keys back into `localStorage`.
 */
export const clearWebStoragePreservingConsent = () => {
  const preserved = {};

  PRESERVE_LOCAL_STORAGE_KEYS.forEach((key) => {
    const value = safeGet(localStorage, key);
    if (value !== null) preserved[key] = value;
  });

  safeClear(localStorage);
  safeClear(sessionStorage);

  Object.entries(preserved).forEach(([key, value]) => {
    safeSet(localStorage, key, value);
  });
};
